import BasicPageHeader from 'components/landing-pages-v2/BasicPageHeader';
import CompareFees from 'components/landing-pages-v2/CompareFees';
import CompareFinalCta from 'components/landing-pages-v2/CompareFinalCta';
import CompareHero from 'components/landing-pages-v2/CompareHero';
import ComparePhotoTestimonials from 'components/landing-pages-v2/ComparePhotoTestimonials';
import CompareReviews from 'components/landing-pages-v2/CompareReviews';
import CompareTable from 'components/landing-pages-v2/CompareTable';
import ComparisonTable from 'components/landing-pages-v2/ComparisonTable';
import ContactSection from 'components/landing-pages-v2/ContactSection';
import FAQsDisplay from 'components/landing-pages-v2/FAQsDisplay';
import FCContactUs from 'components/landing-pages-v2/FCContactUs';
import FCHero from 'components/landing-pages-v2/FCHero';
import FCMap from 'components/landing-pages-v2/FCMap';
import FCMissionStatement from 'components/landing-pages-v2/FCMissionStatement';
import FCProductOptions from 'components/landing-pages-v2/FCProductOptions';
import FCTestimonialCarousel from 'components/landing-pages-v2/FCTestimonialCarousel';
import FCThreeSteps from 'components/landing-pages-v2/FCThreeSteps';
import FCTrustBanner from 'components/landing-pages-v2/FCTrustBanner';
import FCValueTracker from 'components/landing-pages-v2/FCValueTracker';
import Feature5050 from 'components/landing-pages-v2/Feature5050';
import HeaderStandalone from 'components/landing-pages-v2/HeaderStandalone';
import HeroCTA from 'components/landing-pages-v2/HeroCTA';
import HeroV2 from 'components/landing-pages-v2/HeroV2';
import HeroV2WithMarkers from 'components/landing-pages-v2/HeroV2WithMarkers';
import ImageCard from 'components/landing-pages-v2/ImageCard';
import LinkList from 'components/landing-pages-v2/LinkList';
import List from 'components/landing-pages-v2/List';
import LWCHeroCard from 'components/landing-pages-v2/LWCHeroCard';
import LWCImageCard from 'components/landing-pages-v2/LWCImageCard';
import LWCTextSection from 'components/landing-pages-v2/LWCTextSection';
import MapBackgroundHero from 'components/landing-pages-v2/MapBackgroundHero';
import MarketStats from 'components/landing-pages-v2/MarketStats';
// OpenPositions renders the careers listing + JD detail from SSR-fetched
// Rippling data — no useMedia, no iframes, no third-party widgets, so it's
// safe to static-import. Eliminates the dynamic({ssr:false}) hydration flash
// that would otherwise CLS the listing cards / JD body in after first paint.
import OpenPositions from 'components/landing-pages-v2/OpenPositions';
import OptionsComparison from 'components/landing-pages-v2/OptionsComparison';
import PhotoTestimonials from 'components/landing-pages-v2/PhotoTestimonials';
import Reviews from 'components/landing-pages-v2/reviews';
import Spacer from 'components/landing-pages-v2/Spacer';
// StandaloneText is statically imported so its rich-text body — including
// in-content `id` anchors authored via `lpComponentHeaderWithId` — is present
// in the SSR'd HTML. Deferring it to client render breaks deep links like
// /privacy#ccpa because the anchor target does not exist when the browser
// performs its hash scroll on first paint.
import StandaloneText from 'components/landing-pages-v2/StandaloneText';

import { dynamicEntry } from './dynamicEntry';
import type IEntryLookup from './entries';

const Calculator = dynamicEntry(() => import('components/landing-pages-v2/Calculator'));
const FeatureCarousel = dynamicEntry(
  () => import('components/landing-pages-v2/FeatureCarousel/FeatureCarousel'),
);
// CarouselCards swaps mobile/desktop subtrees via useMedia, so it stays
// client-only.
const CarouselCards = dynamicEntry(
  () => import('components/landing-pages-v2/CarouselSection/CarouselCards'),
);
const CompareVideoTestimonial = dynamicEntry(
  () => import('components/landing-pages-v2/CompareVideoTestimonial'),
);
// import CountdownTimer from 'components/landing-pages-v2/CountdownTimer';
const CTAStandalone = dynamicEntry(() => import('components/landing-pages-v2/CTAStandalone'));
// Browser-only carousel, media, iframe, and third-party widget runtimes stay client-only.
const ExternalLinks = dynamicEntry(() => import('components/articles-v2/ExternalLinks'));
const Multicard = dynamicEntry(() => import('components/landing-pages-v2/Multicard'));
const Quotation = dynamicEntry(() => import('components/landing-pages-v2/Quotation'));
const SuperIframe = dynamicEntry(() => import('components/landing-pages-v2/SuperIframe'));
const TrustpilotWidget = dynamicEntry(() => import('components/shared/TrustpilotWidget'));
const VideoPlayer = dynamicEntry(() => import('components/landing-pages-v2/VideoPlayer'));
const TimeSavingsCalculator = dynamicEntry(
  () => import('components/landing-pages-v2/TimeSavingsCalculator'),
);
const TextBanner = dynamicEntry(() => import('components/landing-pages-v2/TextBanner'));
// CustomBlock eagerly imports its registered blocks (e.g. ConquestingOrchard
// branches on useMedia), so it stays client-only until those blocks are
// CSS-stable.
const CustomBlock = dynamicEntry(() => import('components/landing-pages-v2/CustomBlock'));

const landingPageV2EntryLookup: IEntryLookup = {
  block: {
    compareHero: CompareHero,
    compareTable: CompareTable,
    compareFees: CompareFees,
    compareReviews: CompareReviews,
    compareVideoTestimonial: CompareVideoTestimonial,
    comparePhotoTestimonials: ComparePhotoTestimonials,
    compareFinalCta: CompareFinalCta,
    lpComponentMapBackgroundHero: MapBackgroundHero,
    lpComponentCarouselCards: CarouselCards,
    lpComponentImageCard: ImageCard,
    lpComponentFaqDisplay: FAQsDisplay,
    lpComponentHeroV2: HeroV2,
    lpComponentContactSection: ContactSection,
    lpComponentLwcTextSection: LWCTextSection,
    lpComponentLwcHeroCard: LWCHeroCard,
    lpComponentLwcImageCard: LWCImageCard,
    lpComponentHeroV2WithMarkers: HeroV2WithMarkers,
    lpComponentCTAStandalone: CTAStandalone,
    lpComponentComparisonTable: ComparisonTable,
    lpComponentFeature5050: Feature5050,
    lpComponentFeatureCarousel: FeatureCarousel,
    lpComponentHeaderStandalone: HeaderStandalone,
    lpComponentHeroCta: HeroCTA,
    lpComponentList: List,
    lpComponentStandaloneText: StandaloneText,
    lpComponentMulticard: Multicard,
    lpComponentBasicPageHeader: BasicPageHeader,
    lpComponentLinkList: LinkList,
    lpComponentQuotation: Quotation,
    lpComponentSpacer: Spacer,
    lpComponentOpenPositions: OpenPositions,
    lpComponentOptionsComparison: OptionsComparison,
    lpComponentOpendoorReviews: Reviews,
    lpComponentCalculator: Calculator,
    lpComponentTimeSavingsCalculator: TimeSavingsCalculator,
    // lpComponentCountdownTimer: CountdownTimer,
    lpComponentVideoPlayer: VideoPlayer,
    lpComponentMarketStats: MarketStats,
    trustpilotWidget: TrustpilotWidget,
    componentRelatedExternalLinks: ExternalLinks,
    fcHero: FCHero,
    fcTrustBanner: FCTrustBanner,
    fcValueTracker: FCValueTracker,
    fcThreeSteps: FCThreeSteps,
    fcProductOptions: FCProductOptions,
    fcTestimonialCarousel: FCTestimonialCarousel,
    fcMap: FCMap,
    fcMissionStatement: FCMissionStatement,
    fcContactUs: FCContactUs,
    lpComponentTextBanner: TextBanner,
    lpComponentCustomBlock: CustomBlock,
    lpComponentPhotoTestimonials: PhotoTestimonials,
    componentSuperIframe: SuperIframe,
  },
  inline: {},
};

export default landingPageV2EntryLookup;
